import { AppRoute, buildLoginRoute } from '@/constants/routes';
import { normalizeAppPath } from '@/lib/config/webApp';

import type { RequiredRole, UserRole, WebAdminResources, WebSessionResponse } from './types';

type ResolveRouteAccessArgs = {
    pathname: string;
    next: string | null;
    requiredRole?: RequiredRole;
    requiresAdminAccess?: boolean;
    requiredAdminResource?: keyof WebAdminResources;
    requiredAdminAccess?: 'read' | 'full';
    session: WebSessionResponse;
};

type RouteAccessDecision = { kind: 'allow' } | { kind: 'redirect'; href: string };

const getIsRoleAllowed = ({ requiredRole, role }: { requiredRole: RequiredRole; role: UserRole | null }) => {
    if (requiredRole === 'SUPER_ADMIN') {
        return role === 'SUPER_ADMIN';
    }

    return role === 'ADMIN' || role === 'SUPER_ADMIN';
};

const ACCESS_RANK = {
    none: 0,
    read: 1,
    full: 2,
} as const;

export const sanitizeNextPath = (value: string | null | undefined) => {
    return normalizeAppPath(value);
};

export const resolveRouteAccess = ({
    pathname,
    next,
    requiredRole,
    requiresAdminAccess,
    requiredAdminResource,
    requiredAdminAccess = 'read',
    session,
}: ResolveRouteAccessArgs): RouteAccessDecision => {
    const normalizedNext = sanitizeNextPath(next);
    const normalizedPathname = normalizeAppPath(pathname) ?? pathname;
    const isPublicPath = normalizedPathname === AppRoute.Home || normalizedPathname === AppRoute.Login;

    if (isPublicPath) {
        if (session.authenticated) {
            return {
                kind: 'redirect',
                href: AppRoute.Chats,
            };
        }

        return {
            kind: 'allow',
        };
    }

    if (!session.authenticated) {
        const target = normalizedNext ?? normalizedPathname;

        return {
            kind: 'redirect',
            href: buildLoginRoute({ nextPath: target }),
        };
    }

    if (requiredRole && !getIsRoleAllowed({ requiredRole, role: session.role })) {
        return {
            kind: 'redirect',
            href: AppRoute.Chats,
        };
    }

    if (requiresAdminAccess) {
        if (!session.admin?.canAccessAdmin) {
            return {
                kind: 'redirect',
                href: AppRoute.Chats,
            };
        }

        if (requiredAdminResource) {
            const granted = session.admin.resources[requiredAdminResource] ?? 'none';

            if (ACCESS_RANK[granted] < ACCESS_RANK[requiredAdminAccess]) {
                return {
                    kind: 'redirect',
                    href: AppRoute.Chats,
                };
            }
        }
    }

    return {
        kind: 'allow',
    };
};
